import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { SVGFC } from '@repo/toolkit/types'
import React, { useEffect, useState } from 'react'

import { XIcon } from '../../icons'
import Typography from './typography'

interface ProgressBarProps {
  progress: number // Progress value between 0 and 100
  colorStart: TailwindColor // Start color of the gradient
  colorEnd: TailwindColor // End color of the gradient
  height?: number // Height of the progress bar in pixels
  text: string // Optional message to display
  animationMs?: number
  reverted?: boolean
  customIcon?: SVGFC
  title?: string
  onIconClick?: () => void
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  colorStart, // Default start color
  colorEnd, // Default end color
  height = 5, // Default height in pixels
  text,
  reverted,
  customIcon,
  title,
  animationMs = 2000,
  onIconClick
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0)

  useEffect(() => {
    // Animate progress bar to the target value
    setAnimatedProgress(progress)
  }, [progress])

  const Icon = customIcon || XIcon

  return (
    <div className="w-full">
      <div className="flex items-center">
        <div
          className={cn('w-full bg-grey-light rounded overflow-hidden', height && `h-${height}`)}
        >
          <div
            style={{
              width: `${animatedProgress}%`,
              backgroundImage: `linear-gradient(to right, ${colorStart}, ${colorEnd})`,
              transition: `width ${animationMs}ms ease-in-out`
            }}
            className="h-full rounded"
          />
        </div>

        {onIconClick && (
          <button
            onClick={onIconClick}
            className="ml-2 text-grey hover:text-grey-dark transition-colors"
            aria-label="Dismiss"
          >
            <Icon color={tailwindColors.grey.lighter} width="18px" height="18px" />
          </button>
        )}
      </div>

      <div
        className={cn('flex w-full items-center justify-between mt-2', {
          'flex-row-reverse': reverted
        })}
      >
        <Typography variant={'tag'} color={tailwindColors.black.DEFAULT} className="text-sm">
          {`${animatedProgress}% ${text}`}
        </Typography>

        <Typography variant={'tag'} color={tailwindColors.black.DEFAULT} className="text-sm">
          {title ?? '100%'}
        </Typography>
      </div>
    </div>
  )
}

export default ProgressBar
